import { NetworkStatus } from '@apollo/client'
import { FC, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import BackgroundBox from '../../components/custom_components/BackgroundBox'
import { KudosByReplyerFilterInput } from '../../types'
import { KudosDashboardView, KudosTable } from './components/KudosTable'
import { KudosReplyerInfoBox } from './components/KudosReplyerInfoBox'
import PerSpacer from '../../components/custom_components/Spacer'
import { useKudosGivenByReplyer } from '../../graphql/kudos/useKudosQueries'
import { KUDOS_DASHBOARD_PAGE_SIZE } from '../../graphql/kudos/util'
import { NewLoadingAndErrors } from '../../components/custom_components/LoadingAndErrors/NewLoadingAndErrors'
import { ShowMoreButton } from '../reports/table/ShowMoreButton'

export const KudosReplyerManagementPage: FC = () => {
  const { t } = useTranslation()
  const [filters, setFilters] = useState<KudosByReplyerFilterInput>({
    pagination: {
      offset: 0,
      limit: KUDOS_DASHBOARD_PAGE_SIZE,
    },
  })
  const [firstLoadingDone, setFirstLoadingDone] = useState(false)

  const { data, loading, error, fetchMore, networkStatus } = useKudosGivenByReplyer(filters)

  useEffect(() => {
    if (!!data?.getKudosGivenByReplyer) {
      setFirstLoadingDone(true)
    }
  }, [data])

  const handleFiltersChange = (newFilters: KudosByReplyerFilterInput) => {
    setFilters(newFilters)
  }

  const loadMore = () => {
    if (data && data.getKudosGivenByReplyer.hasMore) {
      fetchMore({
        variables: {
          filters: {
            ...filters,
            pagination: {
              offset: data.getKudosGivenByReplyer.nextOffset || 0,
              limit: KUDOS_DASHBOARD_PAGE_SIZE,
            },
          },
        },
        updateQuery: (previousResult, { fetchMoreResult }) => {
          if (!fetchMoreResult) {
            return previousResult
          }

          const previousKudos = previousResult.getKudosGivenByReplyer.kudos
          const fetchMoreKudos = fetchMoreResult.getKudosGivenByReplyer.kudos

          fetchMoreResult.getKudosGivenByReplyer.kudos = [...previousKudos, ...fetchMoreKudos]

          return { ...fetchMoreResult }
        },
      })
    }
  }

  const isLoadingMore = networkStatus === NetworkStatus.fetchMore
  const showMoreLayoutActive = !!data?.getKudosGivenByReplyer?.hasMore && (!loading || isLoadingMore)

  if ((error || loading) && !firstLoadingDone) {
    return (
      <BackgroundBox r height="fullPage">
        <NewLoadingAndErrors isLoading={loading} error={error} hasNoData={false} showSpinner t={t} />
      </BackgroundBox>
    )
  }

  return (
    <>
      <KudosReplyerInfoBox filters={filters} onFiltersChange={handleFiltersChange} />
      <PerSpacer mb="xs" />
      <KudosTable
        kudos={data?.getKudosGivenByReplyer?.kudos}
        loading={loading}
        isLoadingMore={isLoadingMore}
        error={error}
        filters={filters}
        showMoreButtonVisible={showMoreLayoutActive}
        kudosDashboardView={KudosDashboardView.replyer}
      />

      <ShowMoreButton visible={showMoreLayoutActive} loadingMore={isLoadingMore} onLoadMore={loadMore} />
    </>
  )
}
